import jsPDF from 'jspdf';
import { loadImageAsBase64 } from '../invoice-generator/imageUtils';
import { OattInvoice, NspCoreInfo } from './types';
import { formatOattAmount, formatOattAmountAlways } from './formatters';
import {
  MARGIN_LEFT,
  MARGIN_RIGHT,
  RIGHT_EDGE,
  PAGE_WIDTH,
  PAGE_HEIGHT,
  DOLLAR_SIGN_X,
  AMOUNT_X,
  hline,
  box,
} from './layout';
import { getCustomerConfig } from './customerConfig';
// ── Header ─────────────────────────────────────────────────────────────────────

/**
 * Render the 3-column invoice header:
 * [ Logo | NSP Address | Generator Exports/Imports ]
 *
 * @returns The y position after the header block (startY + header height + gap)
 */
export const renderOattHeader = async (
  doc: jsPDF,
  startY: number,
  invoice: OattInvoice,
  primaryIssuer?: NspCoreInfo | null,
): Promise<number> => {
  const HDR_H = 35;

  const C1W = 40;  // Logo
  const C2W = 44;  // NSP Address
  const C4W = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT - C1W - C2W;

  const C1X = MARGIN_LEFT;
  const C2X = C1X + C1W;
  const C4X = C2X + C2W;

  // Outer border
  box(doc, MARGIN_LEFT, startY, PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT, HDR_H, 0.4);

  // ── Col 1: Logo ──────────────────────────────────────────────────────────────
  box(doc, C1X, startY, C1W, HDR_H);

  // Prefer the Azure-hosted logo URL; fall back to the bundled public asset.
  const logoUrl = primaryIssuer?.logoUrl || '/NSP_FullColour_Screen.png';
  const logoData = await loadImageAsBase64(logoUrl);
  if (logoData) {
    // Centre the logo with a small margin, preserving aspect ratio
    const logoW = C1W - 6;
    const logoH = logoW * (766 / 2379); // native aspect ratio of NSP logo
    const logoX = C1X + (C1W - logoW) / 2;
    const logoY = startY + (HDR_H - logoH) / 2;
    doc.addImage(logoData, 'PNG', logoX, logoY, logoW, logoH);
  }

  // ── Col 2: Address ───────────────────────────────────────────────────────
  box(doc, C2X, startY, C2W, HDR_H);

  const paddingX = 2;
  const maxWidth = C2W - (paddingX * 2);

  const addressLines: string[] = [
    primaryIssuer?.companyName,
    primaryIssuer?.address,
    [primaryIssuer?.city, primaryIssuer?.province, primaryIssuer?.postalCode]
      .filter(Boolean)
      .join(', '),
  ].filter(Boolean) as string[];

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);

  // Collect all wrapped lines first so we can centre the block vertically
  const allAddrLines: string[] = [];
  for (const line of addressLines) {
    const wrapped: string[] = doc.splitTextToSize(line, maxWidth);
    allAddrLines.push(...wrapped);
  }

  const addrLineSpacing = 4.5; // mm between baselines
  const addrCapHeight = 8 * 0.7 * (25.4 / 72); // ~2 mm
  const addrBlockHeight = (allAddrLines.length - 1) * addrLineSpacing + addrCapHeight;
  let addrY = startY + (HDR_H - addrBlockHeight) / 2 + addrCapHeight;

  for (const wl of allAddrLines) {
    doc.text(wl, C2X + paddingX, addrY);
    addrY += addrLineSpacing;
  }

  // ── Col 4: single merged box — "OATT Reservation Billing" ───────────────────
  // Font sized so the wrapped text block fills ~50–66% of the box height.
  // At 26pt the text wraps to 2 lines whose combined block ≈ 50% of HDR_H.
  box(doc, C4X, startY, C4W, HDR_H);

  const TITLE_FONT_SIZE = 14; // pt
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(TITLE_FONT_SIZE);
  doc.setTextColor(0, 0, 0);

  const titleText = 'OATT Reservation Billing';
  const titleLines: string[] = doc.splitTextToSize(titleText, C4W - 6);

  // mm per point conversion
  const ptToMm = 25.4 / 72;
  const lineSpacingMm = TITLE_FONT_SIZE * 1.2 * ptToMm;
  const capHeightMm   = TITLE_FONT_SIZE * 0.7 * ptToMm;
  const blockHeightMm = (titleLines.length - 1) * lineSpacingMm + capHeightMm;

  // First baseline vertically centred inside the box
  const firstBaselineY = startY + (HDR_H - blockHeightMm) / 2 + capHeightMm;
  const midX = C4X + C4W / 2;

  titleLines.forEach((line: string, i: number) => {
    doc.text(line, midX, firstBaselineY + i * lineSpacingMm, { align: 'center' });
  });

  return startY + HDR_H + 5;
};

// ── Revision notice ────────────────────────────────────────────────────────────

/**
 * Render an orange "REVISED — Revision N" banner when the invoice is a revision.
 *
 * @returns Updated y position (unchanged if not a revision)
 */
export const renderRevisionNotice = (
  doc: jsPDF,
  invoice: OattInvoice,
  y: number,
): number => {
  if (!invoice.is_revision) return y;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(180, 60, 0);
  doc.text(`REVISED — Revision ${invoice.revision_no}`, MARGIN_LEFT, y);
  return y + 7;
};

// ── Exports heading ────────────────────────────────────────────────────────────

/**
 * Render the customer heading, company address, vendor address, and
 * Account Number / Service for Month metadata row.
 *
 * Heading format: "<CustomerCode> - <CompanyName>" (or "<CustomerCode> Exports" fallback)
 * Company address: standard mailing format from primaryIssuer (NSP)
 * Vendor address: address_line1/2/3 from oatt_vendor_info (customer's civic address)
 *
 * @returns Updated y position after the heading block
 */
export const renderExportsHeading = (
  doc: jsPDF,
  invoice: OattInvoice,
  y: number,
  acctNo: string,
  displayMonthLabel: string,
  primaryIssuer?: NspCoreInfo | null,
): number => {
  // ── Heading: "NEMC - Nalcor Energy Marketing Company" or fallback ────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);

  const headingLabel = invoice.company_name
    ? `${invoice.customer_code} - ${invoice.company_name}`
    : (getCustomerConfig(invoice.customer_code).sectionHeading || `${invoice.customer_code} Exports`);

  doc.text(headingLabel, MARGIN_LEFT, y);
  doc.setLineWidth(0.4);
  doc.setDrawColor(0);
  doc.line(MARGIN_LEFT, y + 1, MARGIN_LEFT + doc.getTextWidth(headingLabel), y + 1);
  y += 7;

  // ── Company address (NSP / primaryIssuer) in standard mailing format ─────────
  if (primaryIssuer) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);

    const companyLines: string[] = [
      primaryIssuer.companyName,
      primaryIssuer.address,
      [primaryIssuer.city, primaryIssuer.province, primaryIssuer.postalCode]
        .filter(Boolean)
        .join(', '),
    ].filter(Boolean) as string[];

    for (const line of companyLines) {
      doc.text(line, MARGIN_LEFT, y);
      y += 4.5;
    }
    y += 2;
  }

  // ── Vendor civic address (from oatt_vendor_info: address_line1/2/3) ──────────
  const vendorLines = [invoice.address_line1, invoice.address_line2, invoice.address_line3].filter(Boolean) as string[];
  if (vendorLines.length > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);

    for (const line of vendorLines) {
      doc.text(line, MARGIN_LEFT, y);
      y += 4.5;
    }
    y += 2;
  }

  // ── Account Number (left) | Service for Month (right of centre) ───────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  const acctLabel = 'Account Number:';
  doc.text(acctLabel, MARGIN_LEFT, y);

  doc.setFont('helvetica', 'normal');
  doc.text(acctNo, MARGIN_LEFT + doc.getTextWidth(acctLabel) + 3, y);

  const serviceLabelX = MARGIN_LEFT + 70;
  doc.setFont('helvetica', 'bold');
  doc.text('Service for Month:', serviceLabelX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(displayMonthLabel, serviceLabelX + doc.getTextWidth('Service for Month:') + 3, y);
  y += 7;

  return y;
};

// ── Schedule section ───────────────────────────────────────────────────────────

/** Render a label+amount row. Label is left-aligned; $ and amount are right-aligned. */
const renderScheduleRow = (
  doc: jsPDF,
  y: number,
  label: string,
  value: number | null | undefined,
  indent: number,
  bold = false,
): number => {
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  doc.text(label, MARGIN_LEFT + indent, y);
  doc.text('$', DOLLAR_SIGN_X, y, { align: 'right' });
  doc.text(formatOattAmount(value ?? 0), AMOUNT_X, y, { align: 'right' });
  return y + 5.5;
};

/** Render a bold section heading (no amount). */
const renderSectionHead = (doc: jsPDF, y: number, label: string): number => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  doc.text(label, MARGIN_LEFT + 4, y);
  return y + 6;
};

/**
 * Render the "Open Access Transmission Tariff" schedule breakdown section,
 * including Transmission Service and Ancillary Services sub-sections.
 *
 * @returns Updated y position after the section
 */
export const renderScheduleSection = (
  doc: jsPDF,
  invoice: OattInvoice,
  y: number,
): number => {
  // Section heading — bold, underlined
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  y += 5;
  const oattHeading = 'Open Access Transmission Tariff :';
  doc.text(oattHeading, MARGIN_LEFT, y);
  doc.setLineWidth(0.35);
  doc.setDrawColor(0);
  doc.line(MARGIN_LEFT, y + 1, MARGIN_LEFT + doc.getTextWidth(oattHeading), y + 1);
  y += 7;

  // Transmission Service
  y = renderSectionHead(doc, y, 'Transmission Service:');
  y = renderScheduleRow(doc, y, 'Schedule 7 - PTP Transmission Service / Firm', invoice.total_firm_transmission_charge, 12);
  y = renderScheduleRow(doc, y, 'Schedule 8 - PTP Transmission Service / Non-Firm', invoice.total_nonfirm_transmission_charge, 12);
  y += 2;

  // Ancillary Services
  y = renderSectionHead(doc, y, 'Ancillary Services:');
  y = renderScheduleRow(doc, y, 'Schedule 1 - Scheduling, System Control, & Dispatch', invoice.total_ancillary_schedule1, 12);
  y = renderScheduleRow(doc, y, 'Schedule 2 - Reactive Control and Voltage Control', invoice.total_ancillary_schedule2, 12);
  y += 4;

  // Miscellaneous Charges — only shown when a charge or comment exists
  if (invoice.misc_charge || invoice.misc_comment) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    doc.text('Miscellaneous Charges:', MARGIN_LEFT + 4, y);
    doc.setFont('helvetica', 'normal');
    doc.text('$', DOLLAR_SIGN_X, y, { align: 'right' });
    doc.text(formatOattAmount(invoice.misc_charge ?? 0), AMOUNT_X, y, { align: 'right' });
    y += 5.5;

    if (invoice.misc_comment) {
      const miscLabel = 'Comment:';
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(miscLabel, MARGIN_LEFT + 12, y);
      doc.setFont('helvetica', 'normal');
      const miscTextX = MARGIN_LEFT + 12 + doc.getTextWidth(miscLabel) + 2;
      const commentLines: string[] = doc.splitTextToSize(invoice.misc_comment, RIGHT_EDGE - miscTextX);
      commentLines.forEach((line: string) => {
        doc.text(line, miscTextX, y);
        y += 5;
      });
      y += 2;
    }
  }

  return y;
};

// ── Totals block ───────────────────────────────────────────────────────────────

/**
 * Render the OATT Charges Total, HST, and Total rows with horizontal rule
 * separators above and below the total.
 *
 * @returns Updated y position after the totals block
 */
export const renderTotalsBlock = (
  doc: jsPDF,
  invoice: OattInvoice,
  y: number,
): number => {
  const LABEL_X = MARGIN_LEFT + 55;


  // Rule above OATT Charges Total
  hline(doc, MARGIN_LEFT, y, RIGHT_EDGE, 0.4);
  y += 5.5;

  // OATT Charges Total (base + misc charge)
  const miscCharge = invoice.misc_charge ?? 0;
  const displayOattTotal = invoice.oatt_charges_total + miscCharge;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('OATT Charges Total :', LABEL_X, y);
  doc.text('$', DOLLAR_SIGN_X, y, { align: 'right' });
  doc.text(formatOattAmountAlways(displayOattTotal), AMOUNT_X, y, { align: 'right' });
  y += 7;

  // HST (rate bold, amount right-aligned)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('HST :', LABEL_X, y);
  doc.setFont('helvetica', 'bold');
  doc.text(`${invoice.hst}%`, LABEL_X + doc.getTextWidth('HST :') + 2, y);
  doc.setFont('helvetica', 'normal');
  doc.text('$', DOLLAR_SIGN_X, y, { align: 'right' });
  doc.text(formatOattAmountAlways(invoice.hst_amount), AMOUNT_X, y, { align: 'right' });
  y += 5;

  // Rule above Total
  hline(doc, MARGIN_LEFT, y, RIGHT_EDGE, 0.4);
  y += 5.5;

  // Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);

  doc.text('Total :', LABEL_X + 30, y);

  doc.text('$', DOLLAR_SIGN_X, y, { align: 'right' });

  doc.text(
    formatOattAmountAlways(invoice.total_bill),
    AMOUNT_X,
    y,
    { align: 'right' }
  );
  y += 5;

  // Rule below Total
  hline(doc, MARGIN_LEFT, y, RIGHT_EDGE, 0.4);
  y += 10;

  return y;
};

// ── Footer notice ──────────────────────────────────────────────────────────────

/**
 * Render the bottom separator line, "IMPORTANT" disclaimer notice, and
 * second separator line.
 */
export const renderFooterNotice = (doc: jsPDF, y: number, vendorComment?: string | null): void => {
  doc.setLineWidth(0.6);
  doc.setDrawColor(0);
  doc.line(MARGIN_LEFT, y, RIGHT_EDGE, y);
  y += 5;

  // Comments (bold, underlined)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(0, 0, 0);
  const commentsLabel = 'Comments';
  doc.text(commentsLabel, MARGIN_LEFT, y);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_LEFT, y + 0.8, MARGIN_LEFT + doc.getTextWidth(commentsLabel), y + 0.8);
  y += 6;

  if (vendorComment) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(vendorComment, RIGHT_EDGE - MARGIN_LEFT);
    doc.text(lines, MARGIN_LEFT, y);
    y += lines.length * 5;
  } else {
    y += 8;
  }

  // Second separator
  doc.setLineWidth(0.6);
  doc.line(MARGIN_LEFT, y, RIGHT_EDGE, y);
};

// ── Page footer ────────────────────────────────────────────────────────────────

/**
 * Render the centered "Generated by test_customer1 OATT Billing System • <date>" footer
 * at the bottom of the page.
 */
export const renderPageFooter = (doc: jsPDF): void => {
  const dateLabel = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(130, 130, 130);
  doc.text(
    `Generated by test_customer1 OATT Billing System  •  ${dateLabel}`,
    PAGE_WIDTH / 2,
    PAGE_HEIGHT - 5,
    { align: 'center' },
  );
};
